import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "../../assets/icons/SearchIcon.svg";
import defaultProfileIcon from "../../assets/icons/defaultProfileIcon.svg";
import gradientPlusIcon from "../../assets/icons/gradientPlusIcon.svg";
import plusIcon from "../../assets/icons/plusIcon.svg";
import whiteEditIcon from "../../assets/icons/whiteEditIcon.svg";
import { CustomDialog, CustomInput } from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import strings from "../../global/constants/StringConstants";
import urls from "../../global/constants/UrlConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
} from "../../helpers/methods";
import {
  selectAccessType,
  selectIsSubscribed,
  selectProfileCount,
  selectRole,
  setProfileIdAction,
} from "../../redux/authSlice";
import history from "../../utils/history";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { theme } from "../../utils/styles";
import ConsentFormDialog from "../Shared/Dialogs/ConsentFormDialog/ConsentFormDialog";
import ContactUsDialog from "../Shared/Dialogs/ContactUsDialog/ContactUsDialog";
import PromoCodeDialog from "../Shared/Dialogs/PromoCodeDialog/PromoCodeDialog";
import UserBanners from "../Shared/UserBanners/UserBanners";
import dashboardStyles from "./Dashboard.styles";
import { getProfile, makeDefault } from "./DashboardService";
import WelcomeScreen from "./WelcomeScreen";

interface ProfileDetails {
  profileId: string;
  name: string;
  birthDate: string;
  default: boolean;
}

const Dashboard = () => {
  const classes = dashboardStyles;
  const dispatch = useAppDispatch();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSubscribed = useAppSelector(selectIsSubscribed);
  const profileCount = useAppSelector(selectProfileCount);
  const isAdmin = useAppSelector(selectRole) === strings.ADMIN;
  const accessType = useAppSelector(selectAccessType);
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<ProfileDetails[]>([]);
  const [initialProfiles, setInitialProfiles] = useState<ProfileDetails[]>([]);
  const [blankProfiles, setBlankProfiles] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openPromo, setOpenPromo] = useState<boolean>(false);
  const [openContactUs, setOpenContactUs] = useState<boolean>(false);
  const [openSubscription, setOpenSubscription] = useState<boolean>(false);
  const [openUpgradePlan, setOpenUpgradePlan] = useState<boolean>(false);
  const [openConsentDialog, setOpenConsentDialog] = useState<boolean>(false);
  const [defaultProfile, setDefaultProfile] = useState<string>("");
  const [isProfilesSelected, setIsProfilesSelected] = useState<boolean>(false);
  const [initialScreenCheck, setInitialScreenCheck] = useState<boolean>(false);

  const canPerformAction = () => {
    return isAdmin || isSubscribed;
  };

  useLayoutEffect(() => {
    setInitialScreenCheck(true);
  }, []);

  useEffect(() => {
    if (!canPerformAction()) {
      setOpenSubscription(true);
    }
  }, [isSubscribed]);

  useEffect(() => {
    if (
      initialScreenCheck &&
      (!isMobileScreen || (isMobileScreen && isProfilesSelected))
    ) {
      getProfiles();
    }
  }, [defaultProfile, isMobileScreen, isProfilesSelected, initialScreenCheck]);

  const hasUnlimitedProfiles = () => {
    if (
      isAdmin ||
      [strings.SUBSCRIPTION_BASIC, strings.SUBSCRIPTION_PREMIUM].includes(
        accessType
      )
    ) {
      return true;
    }
    return false;
  };

  const getProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await getProfile();
      setProfiles(response);
      setInitialProfiles(response);
      const blankProfileCount = 3 - (response.length % 3);
      const blankProfilesArray = Array.from(
        {
          length: hasUnlimitedProfiles()
            ? blankProfileCount
            : profileCount - response.length,
        },
        (_, index) => index + 1
      );
      setBlankProfiles(blankProfilesArray);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleDefaultProfile = async (profileId: string) => {
    try {
      setIsLoading(true);
      const response = await makeDefault(profileId);
      setDefaultProfile(profileId);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleClose = () => {
    setOpenPromo(false);
    setOpenContactUs(false);
    setOpenSubscription(false);
    setOpenUpgradePlan(false);
    setOpenConsentDialog(false);
  };

  const handleProfileClick = (profileId: string) => {
    if (!canPerformAction()) {
      setOpenSubscription(true);
      return;
    }
    history.push(urls.analysisViewPath);
    dispatch(
      setProfileIdAction({
        profileId: profileId,
      })
    );
  };

  const handleSearchParams = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value) {
      searchProfiles(event.target.value.trim());
    } else {
      setProfiles(initialProfiles);
    }
  };

  const searchProfiles = (keyword: string) => {
    const filteredProfiles = profiles.filter((profile) =>
      profile.name.toLowerCase().includes(keyword.toLowerCase())
    );
    setProfiles(filteredProfiles);
  };

  const isAddMoreProfileButtonVisible = () => {
    if (
      isAdmin ||
      [strings.SUBSCRIPTION_BASIC, strings.SUBSCRIPTION_PREMIUM].includes(
        accessType
      )
    ) {
      return false;
    }
    if (
      [strings.BOOK_1, strings.BOOK_2].includes(accessType) &&
      profiles?.length >= 6
    ) {
      return true;
    }
    return false;
  };

  const handleProfilesClick = () => {
    setIsProfilesSelected(true);
  };

  const getSubscriptionButtons = () => {
    return (
      <>
        {!canPerformAction() && (
          <Grid
            container
            spacing={4}
            sx={{ maxWidth: "800px", pt: 2 }}
            pb={{ md: 3, xs: 3 }}
          >
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Box sx={classes.promoCard} onClick={() => setOpenPromo(true)}>
                <Typography sx={classes.headerText}>
                  {t("promoCode")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Box
                sx={classes.promoCard}
                onClick={() => history.push(urls.pricingViewPath)}
              >
                <Typography sx={classes.headerText}>
                  {t("subscribe")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const getDashboardHeader = () => {
    return (
      <>
        <Typography
          sx={classes.italicTypo}
          pt={{ md: !canPerformAction() ? 7 : 5, sm: 0, xs: 0 }}
        >
          {t("dashboardHeader")}
        </Typography>
        <Typography sx={classes.welcomeText} pt={3}>
          {t("dashboardContent")}
        </Typography>
      </>
    );
  };

  const getSearchField = () => {
    return (
      <Box sx={{ width: "100%", maxWidth: "500px", pt: 4 }}>
        <CustomInput
          onChange={debounceEventHandler(
            handleSearchParams,
            strings.SEARCH_TIME_OUT
          )}
          placeHolder={t("searchProfile")}
          InputProps={{
            endAdornment: (
              <Stack mr={1}>
                <img src={searchIcon} width="30px" height="20px" />
              </Stack>
            ),
          }}
        />
      </Box>
    );
  };

  const getProfileCards = () => {
    return (
      <>
        <Grid container sx={{ maxWidth: "1200px" }} justifyContent={"center"}>
          {profiles.map((profile: any) => {
            return (
              <Grid
                item
                xl={4}
                lg={4}
                md={6}
                sm={12}
                xs={12}
                px={{ sm: 1, xs: 0 }}
                key={profile.profileId}
              >
                <Box
                  sx={{
                    ...classes.defaultProfileBox,
                    marginTop: profile.default ? "0px" : "47px",
                  }}
                >
                  {profile.default && (
                    <img
                      src={defaultProfileIcon}
                      style={{
                        height: "40px",
                        borderTopLeftRadius: "20px",
                      }}
                      alt="Module Not Found..."
                    />
                  )}
                </Box>
                <Box sx={classes.profileCard}>
                  <Stack
                    direction={"column"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    spacing={1}
                  >
                    <Box
                      textAlign={"center"}
                      onClick={() =>
                        !canPerformAction()
                          ? setOpenSubscription(true)
                          : handleProfileClick(profile.profileId)
                      }
                    >
                      <Typography sx={classes.profileName}>
                        {profile.name}
                      </Typography>
                      <Typography sx={classes.profileDOB}>
                        DOB: {profile.birthDate}
                      </Typography>
                      <Divider
                        sx={{
                          border: "1px solid rgba(255, 255, 255, 0.20)",
                          width: "100%",
                          height: "0px",
                          marginTop: "10px",
                        }}
                      />
                    </Box>
                    <Box
                      onClick={() =>
                        !canPerformAction()
                          ? setOpenSubscription(true)
                          : history.push(
                              `${urls.profileViewPath}?id=${profile.profileId}`
                            )
                      }
                      sx={classes.centerFlex}
                    >
                      <img
                        src={whiteEditIcon}
                        alt="Module not found..."
                        style={{ width: "20px" }}
                      />
                      <Typography
                        pl={0.5}
                        sx={{
                          ...classes.profileName,
                          textTransform: "capitalize",
                        }}
                      >
                        {t("edit")}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
          {blankProfiles.map((count: number) => {
            return (
              <Grid
                item
                xl={4}
                lg={4}
                md={6}
                sm={12}
                xs={12}
                key={count}
                px={{ sm: 1, xs: 0 }}
              >
                <Box
                  sx={{ ...classes.cardStyle, marginTop: "47px" }}
                  onClick={() =>
                    !canPerformAction()
                      ? setOpenSubscription(true)
                      : history.push(urls.calculateViewPath)
                  }
                >
                  <img
                    src={plusIcon}
                    alt="Module not found..."
                    style={{ width: "50px" }}
                  />
                  <Typography sx={classes.headerText}>
                    {t("addProfile")}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {isAddMoreProfileButtonVisible() && (
          <Box
            display={"flex"}
            pt={4}
            sx={{ cursor: "pointer" }}
            onClick={() => setOpenUpgradePlan(true)}
          >
            <img
              src={gradientPlusIcon}
              alt="Module not found..."
              style={{ width: "30px" }}
            />
            <Typography pl={2} sx={classes.gradientTypo}>
              {t("addMoreProfile")}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  const dialogHeaderContent = () => {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2} pt={5}>
        {openSubscription && <></>}
        {openUpgradePlan && (
          <>
            <Typography sx={classes.dialogHeaderText} px={2}>
              {t("upgradePlanHeader")}!
            </Typography>
            <Divider
              sx={{
                border: "1px solid #222222",
                width: "50%",
                height: "0px",
              }}
            />
          </>
        )}
      </Stack>
    );
  };

  const dialogBodyContent = () => {
    return (
      <Stack
        direction={"column"}
        alignItems={"center"}
        pb={5}
        spacing={2}
        sx={classes.dialogContent}
      >
        {openSubscription && (
          <>
            <Typography sx={classes.subscriptionInfoText}>
              {t("newUserDesc1")}
            </Typography>
            <Typography sx={classes.thankYouText}>{t("thankYou")}!</Typography>
            <Box sx={classes.subscriptionBox}>
              <Typography sx={classes.subscriptionText}>
                {t("newUserDesc2")} 👍
              </Typography>
            </Box>
          </>
        )}
        {openUpgradePlan && (
          <>
            <Typography sx={classes.dialogBodyText} p={2}>
              {t("upgradePlanDesc")}
            </Typography>
            <Button
              sx={{ ...classes.loginBtn, minWidth: "280px", mt: 2 }}
              onClick={() => history.push(urls.pricingViewPath)}
            >
              <Typography sx={{ ...classes.loginBtnTypo, fontSize: "15px" }}>
                {t("upgrade")}
              </Typography>
            </Button>
          </>
        )}
      </Stack>
    );
  };

  const getPromoCodeDialog = () => {
    return (
      <PromoCodeDialog
        open={openPromo}
        setOpen={setOpenPromo}
        setIsLoading={setIsLoading}
      />
    );
  };

  const getConsentDialog = () => {
    return (
      <ConsentFormDialog
        open={openConsentDialog}
        handleClose={handleClose}
        setIsLoading={setIsLoading}
      />
    );
  };

  const getContactUsDialog = () => {
    return (
      <ContactUsDialog
        setIsLoading={setIsLoading}
        setOpenContactUs={setOpenContactUs}
        handleClose={handleClose}
        openContactUs={openContactUs}
      />
    );
  };

  const getSubscriptionDialog = () => {
    return (
      <CustomDialog
        width="600px"
        borderRadius="10px"
        isDialogOpen={openSubscription}
        closable={true}
        closeButtonVisibility={true}
        handleDialogClose={handleClose}
        dialogHeaderContent={dialogHeaderContent()}
        dialogBodyContent={dialogBodyContent()}
      />
    );
  };

  const getUpgradePlanDialog = () => {
    return (
      <CustomDialog
        width="600px"
        borderRadius="10px"
        isDialogOpen={openUpgradePlan}
        closable={true}
        closeButtonVisibility={true}
        handleDialogClose={handleClose}
        dialogHeaderContent={dialogHeaderContent()}
        dialogBodyContent={dialogBodyContent()}
      />
    );
  };

  const getDashboard = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Stack
                  direction={"column"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <UserBanners />
                  {getSubscriptionButtons()}
                  {!isMobileScreen || (isMobileScreen && isProfilesSelected) ? (
                    <>
                      {getDashboardHeader()}
                      {getSearchField()}
                      {getProfileCards()}
                    </>
                  ) : (
                    <WelcomeScreen onProfilesClick={handleProfilesClick} />
                  )}
                  {getPromoCodeDialog()}
                  {getContactUsDialog()}
                  {getSubscriptionDialog()}
                  {getUpgradePlanDialog()}
                  {getConsentDialog()}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };

  return getDashboard();
};

export default Dashboard;
